import React, { useCallback, useEffect, useState } from 'react'
import {
  Button, Checkbox, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tag, Typography, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { get, patch, post } from '../../lib/request'
import { BusinessFormat, RewardType, errMsg } from '../../lib/api'
import { StatusTag } from './shared'

const { Text } = Typography

/**
 * thankyou_v1（批次 2）：答谢类型 / 业态 两个 tab 共用 CRUD 骨架。
 *
 * 后端契约（benefit-plans.ts，只读核对）：
 * - GET /admin/benefit-types | /admin/business-formats：无分页，返回数组
 * - POST：{ code, name, nameEn?, sortOrder?, status?, isDefault?(仅类型) }；code 冲突 409
 * - PATCH /:id：白名单不含 code（编码创建后不可改）；兜底类型唯一，重复设 409
 * - 删除 = 软删 PATCH { status: 'offline' }；无物理 DELETE 端点
 * - 内置行 deletable=false：任何 status 变更一律 409 →「停用」按钮前端置灰（双保险）
 * - POST/PATCH 返回裸行 → 保存后重新 GET 列表刷新，不依赖响应体
 *
 * thankyou_v2（批次 8.2）：类型增「需资质校验」Checkbox（needQualification，设计契约
 * admin-benefit-config-two-level.html 左栏「+ 新建类型（需资质校验勾选）」）。
 * 勾中后绑定该类型的推荐人必须上传资质附件（后端 PATCH /admin/referrers/:id 400 拦截）。
 */
interface Props {
  kind: 'type' | 'format'
  canEdit: boolean
  /** 父级（方案 tab）需要在类型/业态变更后同步刷新自己的下拉选项 */
  onChanged?: () => void
}

const API = { type: '/admin/benefit-types', format: '/admin/business-formats' } as const
const LABEL = { type: '类型', format: '业态' } as const

type Row = (RewardType & BusinessFormat) | null

const TypeFormatTab: React.FC<Props> = ({ kind, canEdit, onChanged }) => {
  const api = API[kind]
  const label = LABEL[kind]

  const [list, setList] = useState<Row[]>([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<Row | 'new'>(null)
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  const fetchList = useCallback(() => {
    setLoading(true)
    get<Row[]>(api)
      .then((d) => setList(Array.isArray(d) ? d : []))
      .catch((err) => message.warning(errMsg(err, `${label}列表加载失败`)))
      .finally(() => setLoading(false))
  }, [api, label])

  useEffect(() => { fetchList() }, [fetchList])

  const openEdit = (r?: Row) => {
    if (r) {
      setEditing(r)
      form.setFieldsValue({
        code: r.code,
        name: r.name,
        nameEn: r.nameEn ?? undefined,
        sortOrder: r.sortOrder ?? 0,
        status: r.status,
        isDefault: kind === 'type' ? !!r.isDefault : undefined,
        // thankyou_v2：需资质校验（仅类型）
        needQualification: kind === 'type' ? !!(r as RewardType).needQualification : undefined,
      })
    } else {
      setEditing('new')
      form.resetFields()
    }
  }

  const save = async () => {
    const v = await form.validateFields()
    // PATCH 白名单不含 code：编辑态不提交编码（后端忽略，前端显式剔除避免误解）
    const payload = {
      ...(editing === 'new' ? { code: v.code } : {}),
      name: v.name,
      nameEn: v.nameEn?.trim() || null,
      sortOrder: v.sortOrder ?? 0,
      status: v.status,
      ...(kind === 'type' ? { isDefault: v.isDefault === true } : {}),
      // thankyou_v2：需资质校验勾选（仅类型；后端白名单含 needQualification）
      ...(kind === 'type' ? { needQualification: v.needQualification === true } : {}),
    }
    setSaving(true)
    try {
      if (editing === 'new') {
        await post(api, payload)
        message.success(`${label}已新增`)
      } else if (editing) {
        await patch(`${api}/${editing.id}`, payload)
        message.success(`${label}已更新`)
      }
      setEditing(null)
      fetchList()
      onChanged?.()
    } catch (err: unknown) {
      // 后端文案原样透出：code 已存在 / 兜底类型已存在 / 内置类型不可停用（409）
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSaving(false)
    }
  }

  const handleOffline = (r: Row) => {
    if (!r) return
    patch(api + '/' + r.id, { status: 'offline' })
      .then(() => {
        message.success(`${label}已停用`)
        fetchList()
        onChanged?.()
      })
      .catch((err: unknown) => message.error(errMsg(err, '停用失败')))
  }

  // 内置行（deletable=false）连停用都被后端 409 拒绝，故编辑弹窗内状态选择一并置灰
  const editingBuiltIn = editing !== null && editing !== 'new' && editing.deletable === false

  const columns: ColumnsType<Row> = [
    { title: '编码', dataIndex: 'code', key: 'code', width: 130 },
    { title: '名称', dataIndex: 'name', key: 'name', render: (v: string) => <Text strong>{v}</Text> },
    {
      title: '英文名',
      dataIndex: 'nameEn',
      key: 'nameEn',
      render: (v?: string | null) => v ?? <Text type="secondary">—</Text>,
    },
    ...(kind === 'type'
      ? [{
          title: '兜底',
          dataIndex: 'isDefault',
          key: 'isDefault',
          width: 72,
          render: (v?: boolean) => (v ? <Text>是</Text> : <Text type="secondary">否</Text>),
        }, {
          // thankyou_v2：需资质校验列（入驻商家 / 商会 = 是）
          title: '需资质校验',
          dataIndex: 'needQualification',
          key: 'needQualification',
          width: 100,
          render: (v?: boolean) =>
            v ? <Tag color="warning">需校验</Tag> : <Text type="secondary">否</Text>,
        }]
      : []),
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 96,
      render: (v: string) => <StatusTag status={v} />,
    },
    ...(canEdit
      ? [{
          title: '操作',
          key: 'actions',
          fixed: 'right' as const,
          width: 180,
          render: (_: unknown, r: Row) =>
            r ? (
              <Space size={4} wrap>
                <Button size="small" style={{ minHeight: 32 }} onClick={() => openEdit(r)}>编辑</Button>
                {/* 无物理删除：删除语义 = 软删停用；内置行停用按钮禁用置灰（后端 409 双保险） */}
                <Popconfirm
                  title={`停用该${label}？`}
                  description={`停用后不再出现在新建方案的下拉选项中（软删，历史记录仍读得出名称）`}
                  okText="停用"
                  cancelText="取消"
                  disabled={r.deletable === false || r.status === 'offline'}
                  onConfirm={() => handleOffline(r)}
                >
                  <Button
                    size="small"
                    danger
                    style={{ minHeight: 32 }}
                    disabled={r.deletable === false || r.status === 'offline'}
                    title={r.deletable === false ? `内置${label}不可停用` : undefined}
                  >
                    停用
                  </Button>
                </Popconfirm>
              </Space>
            ) : null,
        }]
      : []),
  ]

  return (
    <>
      {canEdit && (
        <Space style={{ marginBottom: 14 }} wrap>
          <Button type="primary" onClick={() => openEdit()} style={{ minHeight: 44 }}>
            + 新增{label}
          </Button>
        </Space>
      )}
      <Table<Row>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list}
        scroll={{ x: 'max-content' }}
        style={{ background: '#fff', borderRadius: 12 }}
        pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 条` }}
      />
      <Modal
        title={editing === 'new' ? `新增${label}` : `编辑${label}`}
        open={!!editing}
        onOk={save}
        onCancel={() => setEditing(null)}
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ sortOrder: 0, status: 'published' }}>
          <Form.Item
            name="code"
            label="编码"
            extra={editing === 'new' ? '小写字母 / 数字 / 下划线 / 连字符，≤20；创建后不可修改' : undefined}
            rules={[
              { required: true, message: '编码必填' },
              { pattern: /^[a-z0-9_-]{1,20}$/, message: '小写字母/数字/下划线/连字符，≤20' },
            ]}
          >
            <Input placeholder="例：standard" disabled={editing !== 'new'} maxLength={20} />
          </Form.Item>
          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, message: '名称必填' }, { max: 50, message: '名称不超过 50 字' }]}
          >
            <Input placeholder="例：标准" maxLength={50} />
          </Form.Item>
          <Form.Item name="nameEn" label="英文名（选填）" rules={[{ max: 50, message: '英文名不超过 50 字' }]}>
            <Input placeholder="例：Standard" maxLength={50} />
          </Form.Item>
          {kind === 'type' && (
            <Form.Item
              name="isDefault"
              label="兜底类型"
              valuePropName="checked"
              extra="兜底类型全局唯一；已存在时后端返回 409「兜底类型已存在」"
            >
              <Switch />
            </Form.Item>
          )}
          {kind === 'type' && (
            <Form.Item
              name="needQualification"
              valuePropName="checked"
              extra="勾选后，绑定该类型的推荐人必须上传资质附件（图片 / PDF），否则后端 400 拒保存"
            >
              <Checkbox>需资质校验（入驻商家 / 商会类）</Checkbox>
            </Form.Item>
          )}
          <Form.Item name="sortOrder" label="排序值（越小越靠前）">
            <InputNumber min={0} style={{ width: '100%', minHeight: 44 }} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select
              disabled={editingBuiltIn}
              options={[
                { value: 'draft', label: '草稿' },
                { value: 'published', label: '已发布' },
                { value: 'offline', label: '停用' },
              ]}
            />
          </Form.Item>
          {editingBuiltIn && (
            <Text type="secondary" style={{ fontSize: 12 }}>内置{label}不可停用（后端 409 拦截）</Text>
          )}
        </Form>
      </Modal>
    </>
  )
}

export default TypeFormatTab
